import {
  AirHandlerTypeCodeType,
  ConcealedDuctLocationType,
  CondenserType,
  ConfigurationType,
  LineSetType,
  ZoneType
} from 'data/types';

type ConfigurationForDatabase = {
  uniqueId: string;
  instanceUniqueId?: string | null;
  label: string;
  valid: boolean;
  modified: boolean;
  aiGenerated?: boolean;
  tags?: string[];
};

type CondenserForDatabase = {
  uniqueId: string;
  levelUniqueId: string;
  condenserTypeCode: 'ductless' | 'ducted' | 'hybrid';
  label: string;
  heightOffGround: number;
  noAccessoriesNeeded: boolean;
  modified: boolean;
  valid: boolean;
  positionX: number | null;
  positionY: number | null;
  placementType?: 'ground' | 'wall' | 'roof';
  accessories?: Array<{ productId: number; quantity: number }>;
};

type ZoneForDatabase = {
  uniqueId: string;
  condenserUniqueId?: string | null;
  label: string;
  zoneTypeCode: ZoneType['type'];
  existingSystemUniqueId: string | null;
  valid: boolean;
  modified: boolean;
  spaceUniqueIds: string[];
};

type AirHandlerForDatabase = {
  uniqueId: string;
  zoneUniqueId: string;
  airHandlerTypeCode: AirHandlerTypeCodeType;
  userPositioned?: boolean | null;
  positionX: number;
  positionY: number;
  valid: boolean;
  modified: boolean;
  concealedLocation?: ConcealedDuctLocationType | null;
};

export function transformConfigurationForDatabase(
  config: ConfigurationType,
  instanceUniqueId: string
): ConfigurationForDatabase {
  return {
    uniqueId: config.uniqueId,
    instanceUniqueId,
    label: config.name,
    valid: config.valid ?? false,
    modified: config.modified ?? false,
    aiGenerated: config.aiGenerated,
    tags: undefined
  };
}

export function transformCondensersForDatabase(
  condensers: CondenserType[],
  defaultLevelUniqueId: string
): CondenserForDatabase[] {
  return condensers.map((condenser) => ({
    uniqueId: condenser.uniqueId,
    levelUniqueId: condenser.levelUniqueId ?? defaultLevelUniqueId,
    condenserTypeCode: condenser.type,
    label: condenser.name,
    heightOffGround: condenser.heightOffGround ?? 0,
    noAccessoriesNeeded: condenser.noAccessoriesNeeded ?? false,
    modified: condenser.modified ?? false,
    valid: condenser.valid ?? false,
    positionX: condenser.position?.x ?? 0,
    positionY: condenser.position?.y ?? 0,
    placementType: condenser.placementType,
    accessories: condenser.selectedAccessories?.map((acc) => ({
      productId: acc.productId,
      quantity: acc.quantity
    }))
  }));
}

export function transformZonesForDatabase(
  condensers: CondenserType[]
): ZoneForDatabase[] {
  const zones: ZoneForDatabase[] = [];

  condensers.forEach((condenser) => {
    condenser.zones.forEach((zone) => {
      zones.push({
        uniqueId: zone.uniqueId,
        condenserUniqueId: condenser.uniqueId,
        label: zone.name,
        zoneTypeCode: zone.type,
        existingSystemUniqueId: zone.existingSystemId ?? null,
        valid: zone.valid ?? false,
        modified: zone.modified ?? false,
        spaceUniqueIds: zone.zoneItems.map((item) => item.structureItemId)
      });
    });
  });

  return zones;
}

export function transformAirHandlersForDatabase(
  condensers: CondenserType[]
): AirHandlerForDatabase[] {
  const airHandlers: AirHandlerForDatabase[] = [];

  condensers.forEach((condenser) => {
    condenser.zones.forEach((zone) => {
      if (zone.airHandler) {
        airHandlers.push({
          uniqueId: zone.airHandler.uniqueId,
          zoneUniqueId: zone.uniqueId,
          airHandlerTypeCode: zone.airHandler.airHandlerTypeCode,
          userPositioned: zone.airHandler.userPositioned,
          positionX: zone.airHandler.positionX,
          positionY: zone.airHandler.positionY,
          valid: zone.airHandler.valid ?? false,
          modified: zone.airHandler.modified ?? false,
          concealedLocation: zone.airHandler.concealedLocation ?? null
        });
      }
    });
  });

  return airHandlers;
}

export function transformLineSetsForDatabase(
  lineSets: LineSetType[] | undefined
): LineSetType[] {
  if (!lineSets) return [];
  return lineSets.map((lineSet) => ({
    ...lineSet,
    points: lineSet.points.map((point, index) => ({
      ...point,
      indexNumber: index
    }))
  }));
}

export function extractAllCondenserUniqueIds(
  configs: ConfigurationType[]
): Set<string> {
  const ids = new Set<string>();
  configs.forEach((config) => {
    config.condensers.forEach((c) => ids.add(c.uniqueId));
  });
  return ids;
}

export function extractAllZoneUniqueIds(
  configs: ConfigurationType[]
): Set<string> {
  const ids = new Set<string>();
  configs.forEach((config) => {
    config.condensers.forEach((condenser) => {
      condenser.zones.forEach((zone) => ids.add(zone.uniqueId));
    });
  });
  return ids;
}

export function extractAllAirHandlerUniqueIds(
  configs: ConfigurationType[]
): Set<string> {
  const ids = new Set<string>();
  configs.forEach((config) => {
    config.condensers.forEach((condenser) => {
      condenser.zones.forEach((zone) => {
        if (zone.airHandler) {
          ids.add(zone.airHandler.uniqueId);
        }
      });
    });
  });
  return ids;
}

export function extractAllLineSetUniqueIds(
  configs: ConfigurationType[]
): Set<string> {
  const ids = new Set<string>();
  configs.forEach((config) => {
    config.lineSets?.forEach((lineSet) => ids.add(lineSet.uniqueId));
  });
  return ids;
}

function serializeCondenser(condenser: CondenserType): string {
  return JSON.stringify({
    uniqueId: condenser.uniqueId,
    levelUniqueId: condenser.levelUniqueId,
    type: condenser.type,
    name: condenser.name,
    heightOffGround: condenser.heightOffGround,
    noAccessoriesNeeded: condenser.noAccessoriesNeeded,
    modified: condenser.modified,
    valid: condenser.valid,
    position: condenser.position,
    selectedAccessories: condenser.selectedAccessories?.map((a) => ({
      productId: a.productId,
      quantity: a.quantity
    })),
    accessoriesNotNeeded: condenser.accessoriesNotNeeded,
    accessoriesCompleted: condenser.accessoriesCompleted
  });
}

function serializeZone(zone: ZoneType, condenserUniqueId: string): string {
  return JSON.stringify({
    uniqueId: zone.uniqueId,
    condenserUniqueId,
    name: zone.name,
    type: zone.type,
    existingSystemId: zone.existingSystemId ?? null,
    valid: zone.valid,
    modified: zone.modified,
    zoneItems: zone.zoneItems
      .map((item) => ({
        structureItemId: item.structureItemId,
        registerOverride: item.registerOverride ?? null,
        additionalSupplyRuns: (item.additionalSupplyRuns ?? [])
          .slice()
          .sort((a, b) => a.uniqueId.localeCompare(b.uniqueId)),
        servePassively: item.servePassively ?? null,
        supplyRunPoints: item.supplyRunPoints ?? null
      }))
      .sort((a, b) => a.structureItemId.localeCompare(b.structureItemId)),
    returnOverride: zone.returnOverride ?? null,
    additionalReturnRuns: (zone.additionalReturnRuns ?? [])
      .slice()
      .sort((a, b) => a.uniqueId.localeCompare(b.uniqueId)),
    returnRunPoints: zone.returnRunPoints ?? null
  });
}

function serializeAirHandler(zone: ZoneType): string | null {
  if (!zone.airHandler) return null;
  return JSON.stringify({
    uniqueId: zone.airHandler.uniqueId,
    zoneUniqueId: zone.uniqueId,
    airHandlerTypeCode: zone.airHandler.airHandlerTypeCode,
    positionX: zone.airHandler.positionX,
    positionY: zone.airHandler.positionY,
    valid: zone.airHandler.valid,
    modified: zone.airHandler.modified,
    userPositioned: zone.airHandler.userPositioned ?? false,
    concealedLocation: zone.airHandler.concealedLocation ?? null
  });
}

function serializeLineSet(lineSet: LineSetType): string {
  return JSON.stringify({
    uniqueId: lineSet.uniqueId,
    condenserUniqueId: lineSet.condenserUniqueId,
    airHandlerUniqueId: lineSet.airHandlerUniqueId,
    addWire: lineSet.addWire,
    valid: lineSet.valid,
    modified: lineSet.modified,
    points: lineSet.points?.map((p) => ({
      uniqueId: p.uniqueId,
      indexNumber: p.indexNumber,
      positionX: p.positionX,
      positionY: p.positionY,
      positionZ: p.positionZ ?? null,
      levelUniqueId: p.levelUniqueId
    }))
  });
}

export type ConfigurationChanges = {
  changedConfigurations: ConfigurationType[];
  changedCondensers: Array<{
    configurationUniqueId: string;
    condenser: CondenserType;
    defaultLevelUniqueId: string;
  }>;
  changedZones: Array<{
    configurationUniqueId: string;
    zone: ZoneType;
    condenserUniqueId: string;
  }>;
  changedAirHandlers: Array<{
    configurationUniqueId: string;
    zone: ZoneType;
  }>;
  changedLineSets: Array<{
    configurationUniqueId: string;
    lineSet: LineSetType;
  }>;
  deletedConfigurationUniqueIds: string[];
  deletedCondenserUniqueIds: string[];
  deletedZoneUniqueIds: string[];
  deletedAirHandlerUniqueIds: string[];
  deletedLineSetUniqueIds: string[];
};

export function detectConfigurationChanges(
  previousConfigs: ConfigurationType[],
  currentConfigs: ConfigurationType[],
  defaultLevelUniqueId: string
): ConfigurationChanges {
  const changes: ConfigurationChanges = {
    changedConfigurations: [],
    changedCondensers: [],
    changedZones: [],
    changedAirHandlers: [],
    changedLineSets: [],
    deletedConfigurationUniqueIds: [],
    deletedCondenserUniqueIds: [],
    deletedZoneUniqueIds: [],
    deletedAirHandlerUniqueIds: [],
    deletedLineSetUniqueIds: []
  };

  const currentConfigIds = new Set(currentConfigs.map((c) => c.uniqueId));
  const currentCondenserIds = extractAllCondenserUniqueIds(currentConfigs);
  const currentZoneIds = extractAllZoneUniqueIds(currentConfigs);
  const currentAirHandlerIds = extractAllAirHandlerUniqueIds(currentConfigs);
  const currentLineSetIds = extractAllLineSetUniqueIds(currentConfigs);

  previousConfigs.forEach((prevConfig) => {
    if (!currentConfigIds.has(prevConfig.uniqueId)) {
      changes.deletedConfigurationUniqueIds.push(prevConfig.uniqueId);
    }
  });

  const prevCondenserIds = extractAllCondenserUniqueIds(previousConfigs);
  prevCondenserIds.forEach((id) => {
    if (!currentCondenserIds.has(id)) {
      changes.deletedCondenserUniqueIds.push(id);
    }
  });

  const prevZoneIds = extractAllZoneUniqueIds(previousConfigs);
  prevZoneIds.forEach((id) => {
    if (!currentZoneIds.has(id)) {
      changes.deletedZoneUniqueIds.push(id);
    }
  });

  const prevAirHandlerIds = extractAllAirHandlerUniqueIds(previousConfigs);
  prevAirHandlerIds.forEach((id) => {
    if (!currentAirHandlerIds.has(id)) {
      changes.deletedAirHandlerUniqueIds.push(id);
    }
  });

  const prevLineSetIds = extractAllLineSetUniqueIds(previousConfigs);
  prevLineSetIds.forEach((id) => {
    if (!currentLineSetIds.has(id)) {
      changes.deletedLineSetUniqueIds.push(id);
    }
  });

  const previousConfigMap = new Map(
    previousConfigs.map((c) => [c.uniqueId, c])
  );

  currentConfigs.forEach((config) => {
    const prevConfig = previousConfigMap.get(config.uniqueId);

    if (
      !prevConfig ||
      config.name !== prevConfig.name ||
      config.valid !== prevConfig.valid ||
      config.modified !== prevConfig.modified
    ) {
      changes.changedConfigurations.push(config);
    }

    const prevCondenserMap = new Map(
      prevConfig?.condensers.map((c) => [c.uniqueId, c]) ?? []
    );

    config.condensers.forEach((condenser) => {
      const prevCondenser = prevCondenserMap.get(condenser.uniqueId);
      if (
        !prevCondenser ||
        serializeCondenser(condenser) !== serializeCondenser(prevCondenser)
      ) {
        changes.changedCondensers.push({
          configurationUniqueId: config.uniqueId,
          condenser,
          defaultLevelUniqueId
        });
      }
    });

    config.condensers.forEach((condenser) => {
      const prevCondenser = prevConfig?.condensers.find(
        (c) => c.uniqueId === condenser.uniqueId
      );

      condenser.zones.forEach((zone) => {
        const prevZone = prevCondenser?.zones.find(
          (z) => z.uniqueId === zone.uniqueId
        );
        if (
          !prevZone ||
          serializeZone(zone, condenser.uniqueId) !==
            serializeZone(prevZone, condenser.uniqueId)
        ) {
          changes.changedZones.push({
            configurationUniqueId: config.uniqueId,
            zone,
            condenserUniqueId: condenser.uniqueId
          });
        }

        const currentAirHandler = serializeAirHandler(zone);
        const prevAirHandler = prevZone ? serializeAirHandler(prevZone) : null;
        if (currentAirHandler !== prevAirHandler && zone.airHandler) {
          changes.changedAirHandlers.push({
            configurationUniqueId: config.uniqueId,
            zone
          });
        }
      });
    });

    const prevLineSetMap = new Map(
      prevConfig?.lineSets?.map((ls) => [ls.uniqueId, ls]) ?? []
    );

    config.lineSets?.forEach((lineSet) => {
      const prevLineSet = prevLineSetMap.get(lineSet.uniqueId);
      if (
        !prevLineSet ||
        serializeLineSet(lineSet) !== serializeLineSet(prevLineSet)
      ) {
        changes.changedLineSets.push({
          configurationUniqueId: config.uniqueId,
          lineSet
        });
      }
    });
  });

  return changes;
}
